import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";

// 🔧 ENV CHECK (CONFIRMED WORKING)
const isProduction = process.env.REACT_APP_ENV !== "development";
console.log("confirmation", process.env.REACT_APP_ENV, isProduction);

// ⏱ TIMINGS
const IDLE_WARNING_TIME = isProduction
  ? 4 * 60 * 1000 + 40 * 1000 // 4m 40s
  : 29 * 60 * 1000 + 40 * 1000; // 29m 40s

const LOGOUT_TIME = isProduction
  ? 5 * 60 * 1000 // 5m
  : 30 * 60 * 1000; // 30m

export default function IdleTimerProvider({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(20);

  const idleTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const pausedRef = useRef(false);
  const hasLoggedOutRef = useRef(false);
  const initializedRef = useRef(false);

  const location = useLocation();
  const pathname = location.pathname;

  // 🔁 Broadcast activity to other tabs
  const broadcastActivity = () => {
    localStorage.setItem("lastActivity", Date.now().toString());
  };

  // 🔄 Reset timers
  const resetTimers = (broadcast = true) => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);

    setShowWarning(false);
    setCountdown(20);
    pausedRef.current = false;

    if (broadcast) broadcastActivity();

    // ⚠️ Idle warning timer
    idleTimerRef.current = setTimeout(() => {
      pausedRef.current = true;

      // 🔥 CRITICAL FIX: stop absolute logout timer
      clearTimeout(logoutTimerRef.current);

      setShowWarning(true);
      startCountdown();
    }, IDLE_WARNING_TIME);

    // ⏳ Absolute logout timer
    logoutTimerRef.current = setTimeout(() => {
      handleLogout(true);
    }, LOGOUT_TIME);
  };

  // ⏱ Countdown logic
  const startCountdown = () => {
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleLogout(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ✅ Stay here
  const handleStayHere = () => {
    resetTimers();
  };

  // 🚪 Logout (SAFE + GUARDED)
  const handleLogout = (dueToIdle = false) => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    if (window.location.pathname === "/login") return;

    clearTimeout(idleTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);

    localStorage.setItem("loggedOutDueToInactivity", "true");

    const flag = localStorage.getItem("loggedOutDueToInactivity");
    localStorage.clear();
    localStorage.setItem("loggedOutDueToInactivity", flag);
    localStorage.setItem("forceLogout", Date.now().toString());

    window.location.href = "/login";
  };

  // 🧠 MAIN EFFECT
  useEffect(() => {
    if (pathname === "/login") {
      if (!localStorage.getItem("lastActivity")) {
        localStorage.setItem("lastActivity", Date.now().toString());
      }
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keypress",
      "touchstart",
      "scroll",
    ];

    const handleActivity = () => {
      if (pausedRef.current) return;
      broadcastActivity();
      resetTimers(false);
    };

    activityEvents.forEach((event) =>
      window.addEventListener(event, handleActivity)
    );

    if (!localStorage.getItem("lastActivity")) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }

    resetTimers();

    // 🪟 Cross-tab sync
    const handleStorage = (e) => {
      if (e.key === "lastActivity") {
        if (!pausedRef.current) resetTimers(false);
      }
      if (e.key === "forceLogout") {
        handleLogout(false);
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      window.removeEventListener("storage", handleStorage);

      clearTimeout(idleTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [pathname]);

  return (
    <>
      {children}

      <Dialog
        open={showWarning}
        onClose={handleStayHere}
        PaperProps={{
          sx: { borderRadius: "12px", p: 2, textAlign: "center" },
        }}
      >
        <DialogTitle className="font-siromi text-xl text-[#9D0385]">
          Are you still there?
        </DialogTitle>

        <DialogContent>
          <Typography className="font-altisunisa">
            You’ve been idle for a while. You’ll be logged out in{" "}
            <strong>{countdown}</strong> seconds.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={handleStayHere}
            className="bg-[#572575] hover:bg-[#572575] text-white font-altisunisa font-semibold"
            sx={{ px: 3 }}
          >
            Stay Here
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
